import { Component, Input, OnInit } from '@angular/core';

import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getStaffNameDisplay,
  timeStringFromISO8601
} from '../../../../../../../../utils';
import { PotentialDuplicateCustomer, PotentialDuplicateCustomerStatus } from '../../../../../../../../models/inbox';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AccountService } from '../../../../../../../../services/account/account.service';
import { InboxService } from '../../../../../../../../services/inbox/inbox.service';
import { OrganizationConfigService } from '../../../../../../../../services/organization-config/organization-config.service';
import { Router } from '@angular/router';


enum StaffAction {
  merge,
  dismiss,
  hide,
}


@Component({
  selector: 'app-view-potential-duplicate-customer-modal',
  templateUrl: './view-potential-duplicate-customer-modal.component.html',
  styleUrls: ['./view-potential-duplicate-customer-modal.component.css']
})
export class ViewPotentialDuplicateCustomersModalComponent implements OnInit {
  @Input() dupId: number;
  @Input() showHideOption: boolean;
  @Input() savedCallback: (result: CallbackResult) => undefined;
  // The ID of the customer whose profile page this was opened from (if any).
  @Input() thisCustomerId: number = null;

  public dup: PotentialDuplicateCustomer;

  public selectedAction: StaffAction = null;

  public loadingDup = true;
  public loadingSubmit = false;
  public notFound = false;
  public errorMessage = '';
  public serverError: number = null;

  public readonly PotentialDuplicateCustomerStatus = PotentialDuplicateCustomerStatus;
  public readonly StaffAction = StaffAction;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly getStaffNameDisplay = getStaffNameDisplay;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfig: OrganizationConfigService,
    private accountService: AccountService,
    private inboxService: InboxService,
    private router: Router,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    this.loadDup();
  }

  /**
   * Whether a valid input has been selected. Used to determine whether the
   * save button is clickable.
   */
  public validInputSelected(): boolean {
    return [
      StaffAction.merge,
      StaffAction.dismiss,
      StaffAction.hide
    ].includes(this.selectedAction);
  }

  public navigateToMergePage() {
    this.router.navigate(
      ['/customers/merge-customers'],
      {
        queryParams: {
          primaryId: this.dup.existing_customer_id.toString(),
          secondaryId: this.dup.new_customer_id.toString()
        }
      }
    );
    this.activeModal.close();
  }

  public handleSave() {
    this.loadingSubmit = true;

    this.errorMessage = '';

    let savePromise: Promise<void>;
    let successMessage: string;

    if (this.selectedAction === StaffAction.merge) {
      savePromise = this.inboxService.mergePotentialDuplicateCustomer(this.dupId);
      successMessage = 'Customers merged successfully.';
    } else if (this.selectedAction === StaffAction.dismiss) {
      savePromise = this.inboxService.dismissPotentialDuplicateCustomer(this.dupId);
      successMessage = 'Potential duplicate dismissed.';
    } else if (this.selectedAction === StaffAction.hide) {
      savePromise = this.inboxService.hidePotentialDuplicateCustomer(this.dupId);
      successMessage = 'Potential duplicate hidden.';
    } else {
      this.errorMessage = 'Please choose a valid option.';
      this.loadingSubmit = false;
      return;
    }

    savePromise
      .then(() => {
        this.inboxService.loadInboxCounts(true).then();

        const mustRedirect = (
          (this.thisCustomerId === this.dup.new_customer_id) &&
          (this.selectedAction === StaffAction.merge)
        );
        if (mustRedirect) {
          this.router.navigate(
            ['/customers/' + this.dup.existing_customer_id.toString()]
          );
        } else {
          this.savedCallback({
            text: successMessage,
            status: CallbackResultStatus.success
          });
        }

        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to take this action.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  private loadDup() {
    this.loadingDup = true;

    this.inboxService
      .getPotentialDuplicateCustomer(this.dupId)
      .then(dup => { this.dup = dup;})
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingDup = false; });
  }
}
